<template>
    <div v-show="isLoaded">
        <div
            v-if="Array.isArray(eventDetails.details) && eventDetails.details.length > 0"
            class="report-content"
        >
            <div class="graphics">
                <Hide v-model="chartVisibility">
                    <h4>{{ $t('reports.callStatistics') }}</h4>
                </Hide>
                <div v-show="chartVisibility">
                    <div v-if="speeches.length > 0" class="call-speech">
                        <span
                            v-for="(speech, index) in speeches"
                            :key="index"
                            :style="speech.style"
                            :title="speech.number"
                        >
                            <span v-if="speech.isNumberShown"> {{ speech.number }} </span>
                        </span>
                    </div>
                    <div v-if="np2.length > 0" class="call-presence">
                        <span v-for="(np, i) in np2" :key="i" :style="np.style" class="np1">
                            <span
                                v-if="!np.isOutsideText || (np.isOutsideText && !hasNPSpaceLimit(np2, i))"
                                :class="{ 'outside-text': np.isOutsideText }"
                            >
                                {{ $t('reports.ascomEvent.nursePresence2') }}
                            </span>
                        </span>
                    </div>
                    <div v-if="np1.length > 0" class="call-presence">
                        <span v-for="(np, i) in np1" :key="i" :style="np.style" class="np2">
                            <span
                                v-if="!np.isOutsideText || (np.isOutsideText && !hasNPSpaceLimit(np1, i))"
                                :class="{ 'outside-text': np.isOutsideText }"
                            >
                                {{ $t('reports.ascomEvent.nursePresence1') }}
                            </span>
                        </span>
                    </div>
                    <div
                        v-if="isCallWaitingBar"
                        class="call-waiting"
                        :style="{ width: `${callWaitingWidth}%` }"
                    >
                        <span
                            :class="
                                callWaitingWidth > 25 ? 'call-waiting-time-inside' : 'call-waiting-time-right'
                            "
                            >{{ $t('reports.ascomEvent.callWaiting') }}</span
                        >
                    </div>
                    <div class="call-period">
                        <span>{{ $t('reports.ascomEvent.callLivePeriod') }}</span>
                    </div>
                    <div class="ascom-event-timeline">
                        <div
                            v-for="(trace, index) in timelineTrace"
                            :key="index"
                            class="event-line"
                            :style="trace.style"
                        >
                            <div
                                class="event-time-label"
                                :class="{ 'event-time-label-top': isMainLine(index) }"
                            >
                                <span v-if="isMainLine(index) || !hasNeighbor(trace, index)">{{
                                    trace.val
                                }}</span>
                            </div>
                            <div class="event-time-point" :title="trace.val"></div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="detailing">
                <Hide v-model="tableVisibility">
                    <h4>{{ $t('reports.detailed') }}</h4>
                </Hide>
            </div>
            <table v-show="tableVisibility" class="common-table">
                <thead>
                    <tr>
                        <th>{{ $t('reports.ascomEvent.event') }}</th>
                        <th>{{ $t('reports.ascomEvent.dateTime') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(detail, index) in eventDetails.details" :key="index">
                        <td>
                            <img
                                v-if="showColoredIcon(detail.icons[0])"
                                :src="'static/img/' + iconClass(detail.icons[0]) + '.svg'"
                                alt=""
                            />
                            <i
                                v-else
                                :class="['icon-' + iconClass(detail.icons[0]), { canceled: detail.icons[1] }]"
                            ></i>
                            {{ $t('reporting.detail.' + detail.key, detail.params) }}
                        </td>
                        <td>{{ detail.timeFormatted }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <h4 v-else class="data-empty">{{ $t('reports.emptyReport') }}</h4>
    </div>
</template>

<script type="text/babel">
import isEqual from 'lodash-es/isEqual';
import sortBy from 'lodash-es/sortBy';
import uniqBy from 'lodash-es/uniqBy';
import cloneDeep from 'lodash-es/cloneDeep';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import getTime from 'date-fns/getTime';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import mappingState from '../../common/mappingState';
import Hide from '../../common/Hide.vue';
import { showColoredIcon, iconClass } from '../helpers/reportsHelper';
import { formatDateString, secsToReadableTime } from '../../common/helpers';
import { resourceAscomEventReport } from '../../../api/resources';
import { getCancelToken } from '../../../api';
import { ERROR, EVENT_KEYS } from '../../../constants';

const MIN_WIDTH = 0.3;
const ZOOM_ONGOING = 1.2;
const NP_WIDTH_LABEL = 12;

export default {
    components: {
        Hide,
    },

    mixins: [mappingState],
    props: {
        reportParameters: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            eventDetails: [],
            isLoaded: false,
            np1: [],
            np2: [],
            speeches: [],
            timelineTrace: [],
            callAccepted: null,
            chartVisibility: true,
            tableVisibility: true,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        /**
         * Returns call waiting width. This is not displayed, if nurse present starts from 0.
         * @return {number}
         */
        callWaitingWidth() {
            let width;

            if (
                this.np1.length === 0 &&
                this.np2.length === 0 &&
                this.speeches.length === 0 &&
                this.callAccepted === 0
            ) {
                return 100;
            }

            width = Math.min.apply(Math, [
                this.callAccepted ? parseFloat(this.callAccepted) : 100,
                ...(this.np1.length > 0 ? this.np1.map(item => parseFloat(item.style.left)) : [100]),
                ...(this.np2.length > 0 ? this.np2.map(item => parseFloat(item.style.left)) : [100]),
            ]);
            // Display short waiting
            if (width > 0 && width < MIN_WIDTH) {
                width = MIN_WIDTH;
            }

            return width;
        },

        /**
         * Defines the presence of call waiting bar
         * @return {boolean}
         */
        isCallWaitingBar() {
            return (
                ((this.np1.length > 0 && this.np1.every(item => parseFloat(item.style.left) > 0)) ||
                    (this.np2.length > 0 && this.np2.every(item => parseFloat(item.style.left) > 0)) ||
                    parseFloat(this.callAccepted) > 0 ||
                    (this.np1.length === 0 && this.np2.length === 0) ||
                    this.speeches.length > 0) &&
                this.callWaitingWidth > 0
            );
        },
    },

    watch: {
        reportParameters(newParameters, oldParameters) {
            if (!isEqual(newParameters, oldParameters)) {
                this.resetToDefault();
            }
        },
    },

    mounted() {
        this.loadAscomEventReport();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        showColoredIcon,
        iconClass,

        /**
         * Fetch data and init chart if details array is not empty
         */
        async loadAscomEventReport() {
            this.showLoading(true);
            try {
                this.eventDetails = (
                    await resourceAscomEventReport({
                        method: 'post',
                        data: this.reportParameters,
                        cancelToken: this.cancelToken.token,
                    })
                ).data;

                if (this.eventDetails.details && this.eventDetails.details.length > 0) {
                    this.eventDetails.details = this.eventDetails.details.map(detail => ({
                        ...detail,
                        timeFormatted: formatDateString(detail.time),
                    }));

                    this.initAscomEventChart();
                }
                this.$emit('ascomEventEndTime', this.eventDetails.end_time);
            } catch (error) {
                if (error !== 'canceled' && error.data && error.data.error) {
                    this.popup({
                        type: ERROR,
                        header: this.$t('common.error'),
                        text: this.$t(error.data.error),
                    });
                }
            } finally {
                this.isLoaded = true;
                this.showLoading(false);
            }
        },

        /**
         * Init chart drawing. Calculate start and end values for timeline, calculate width's for bar lines.
         */
        initAscomEventChart() {
            let firstSetCall = this.eventDetails.details.find(e => e.key === EVENT_KEYS.CALL);
            let firstSetCallAccepted = this.eventDetails.details.find(
                e => e.key === EVENT_KEYS.CALL_ACCEPTED
            );

            // Remove second and later call and call accept
            let eventsWithoutDuplicate = this.eventDetails.details
                .filter(
                    e =>
                        (e.key === EVENT_KEYS.CALL && e === firstSetCall) ||
                        (e.key === EVENT_KEYS.CALL_ACCEPTED && e === firstSetCallAccepted) ||
                        ![EVENT_KEYS.CALL, EVENT_KEYS.CALL_ACCEPTED].includes(e.key)

                    // Remove call rejected
                )
                .filter(e => e.key !== EVENT_KEYS.CALL_REJECTED);
            let lastEventTime = eventsWithoutDuplicate[eventsWithoutDuplicate.length - 1].time;
            let totalDuration = differenceInMilliseconds(
                parseISO(lastEventTime),
                parseISO(this.eventDetails.start_time)
            );

            if (totalDuration === 0) {
                // Fake totalDuration time for zero
                totalDuration = 180;
            } else if (totalDuration > 0 && this.eventDetails.end_time === null) {
                // If ongoing call and there are events with non-zero totalDuration
                // deliberately reduce the length of the completed events by 20 percent
                totalDuration = totalDuration * ZOOM_ONGOING;
            }

            this.setChartValues(totalDuration);

            this.timelineTrace = uniqBy(
                sortBy([...this.timelineTrace, ...cloneDeep(this.speeches)], 'beginning'),
                e => e.val
            );
            this.setWidthForTimeLineTrace(totalDuration);
        },

        /**
         * Calculate width's for Nurse 1 Present, Nurse 2 Present, Speech's, init generateTimeLine method.
         * @param {number} totalDuration
         */
        setChartValues(totalDuration) {
            let callTraceStart = this.eventDetails.start_time;
            let lastEvent = this.eventDetails.details[this.eventDetails.details.length - 1];
            let callTraceEnd = this.eventDetails.end_time ? this.eventDetails.end_time : lastEvent.time;
            let startedSpeech = null;

            this.eventDetails.details.forEach(event => {
                if (event.key === EVENT_KEYS.NURSE_PRESENT && event.params[0] === 'Nurse 1 present') {
                    this.np1.push({
                        startTime: event.time,
                        style: {
                            width: null,
                            left: `${this.getTimePercent(event.time, callTraceStart, totalDuration)}%`,
                        },
                        eventReferenceId: event.eventReferenceId,
                        isOutsideText: false,
                    });
                    this.addTimeLine(this.timeDiff(callTraceStart, event.time), event.time);
                } else if (event.key === EVENT_KEYS.NURSE_PRESENT && event.params[0] === 'Nurse 2 present') {
                    this.np2.push({
                        startTime: event.time,
                        style: {
                            width: null,
                            left: `${this.getTimePercent(event.time, callTraceStart, totalDuration)}%`,
                        },
                        eventReferenceId: event.eventReferenceId,
                        isOutsideText: false,
                    });
                    this.addTimeLine(this.timeDiff(callTraceStart, event.time), event.time);
                } else if (event.key === EVENT_KEYS.NURSE_PRESENT_CANCEL) {
                    if (
                        this.np1.length > 0 &&
                        this.np1.some(item => item.eventReferenceId === event.eventReferenceId)
                    ) {
                        let npEvent = this.np1.find(item => item.eventReferenceId === event.eventReferenceId);
                        let width =
                            (differenceInMilliseconds(parseISO(event.time), parseISO(npEvent.startTime)) /
                                totalDuration) *
                            100;
                        // Display short nurse present
                        if (width < MIN_WIDTH) {
                            width = MIN_WIDTH;
                        }
                        npEvent.style.width = `${width}%`;
                        npEvent.isOutsideText = width < NP_WIDTH_LABEL;
                    }
                    if (
                        this.np2.length > 0 &&
                        this.np2.some(item => item.eventReferenceId === event.eventReferenceId)
                    ) {
                        let npEvent = this.np2.find(item => item.eventReferenceId === event.eventReferenceId);
                        let width =
                            (differenceInMilliseconds(parseISO(event.time), parseISO(npEvent.startTime)) /
                                totalDuration) *
                            100;
                        // Display short nurse present
                        if (width < MIN_WIDTH) {
                            width = MIN_WIDTH;
                        }
                        npEvent.style.width = `${width}%`;
                        npEvent.isOutsideText = width < NP_WIDTH_LABEL;
                    }
                    this.addTimeLine(this.timeDiff(callTraceStart, event.time), event.time);
                } else if (event.key === EVENT_KEYS.CALL_ACCEPTED && !this.callAccepted) {
                    this.callAccepted = this.getTimePercent(event.time, callTraceStart, totalDuration);
                    this.addTimeLine(this.timeDiff(callTraceStart, event.time), event.time);
                } else if (event.key === EVENT_KEYS.CALL_SPEECH && startedSpeech === null) {
                    startedSpeech = event;
                    this.addTimeLine(this.timeDiff(callTraceStart, event.time), event.time);
                } else if (
                    event.key === EVENT_KEYS.CALL_PARKED &&
                    startedSpeech &&
                    startedSpeech.params[0] === event.params[0]
                ) {
                    let speechStart = this.getTimePercent(startedSpeech.time, callTraceStart, totalDuration);
                    let speechEnd = this.getTimePercent(event.time, callTraceStart, totalDuration);
                    let { width, marginLeft } = this.speechStyle(speechStart, speechEnd);
                    let { diff, duration } = this.timeDiff(callTraceStart, event.time);
                    const speech = {
                        start: speechStart,
                        end: speechEnd,
                        number: event.params[0],
                        val: duration,
                        beginning: diff,
                        time: event.time,
                        tooltip: duration,
                        style: { width: `${width}%`, 'margin-left': `${marginLeft}%` },
                        isNumberShown: width > 5,
                    };
                    this.speeches.push(speech);
                    this.addTimeLine(this.timeDiff(callTraceStart, startedSpeech.time), event.time);
                    startedSpeech = null;
                } else if (this.timeDiff(callTraceStart, event.time).diff === 0) {
                    this.addTimeLine(this.timeDiff(callTraceStart, event.time), event.time);
                }
            });

            // Ongoing speech
            if (startedSpeech !== null) {
                let speechStart = this.getTimePercent(startedSpeech.time, callTraceStart, totalDuration);
                // speechParked was not found in events, calculate end time by callTraceStart time + totalDuration
                let speechEndTime = format(
                    getTime(callTraceStart) + totalDuration,
                    'YYYY-MM-DDTHH:mm:ss.SSS'
                );
                let speechEndPercent = this.getTimePercent(speechEndTime, callTraceStart, totalDuration);
                let { diff, duration } = this.timeDiff(callTraceStart, speechEndTime);
                let { width, marginLeft } = this.speechStyle(speechStart, speechEndPercent);
                this.speeches.push({
                    start: speechStart,
                    end: speechEndPercent,
                    number: startedSpeech.params[0],
                    val: this.eventDetails.end_time === null ? this.$t('reports.ongoing') : duration,
                    beginning: diff,
                    time: lastEvent.time,
                    tooltip: this.eventDetails.end_time === null ? this.$t('reports.ongoing') : duration,
                    style: { width: `${width}%`, 'margin-left': `${marginLeft}%` },
                    isNumberShown: width > 5,
                });
            }

            // Ongoing nurse present 1
            if (this.np1.length > 0 && this.np1.some(np => np.style.width === null)) {
                let np = this.np1.find(np => np.style.width === null);
                let secBeforeStart = getTime(parseISO(np.startTime)) - getTime(parseISO(callTraceStart));
                let width = (totalDuration - secBeforeStart) / totalDuration;
                np.style.width = `${width * 100}%`;
            }

            // Ongoing nurse present 2
            if (this.np2.length > 0 && this.np2.some(np => np.style.width === null)) {
                let np = this.np2.find(np => np.style.width === null);
                let secBeforeStart = getTime(parseISO(np.startTime)) - getTime(parseISO(callTraceStart));
                let width = (totalDuration - secBeforeStart) / totalDuration;
                np.style.width = `${width * 100}%`;
            }

            if (this.eventDetails.incomplete) {
                this.addTimeLine({ diff: totalDuration }, 'Unknown');
            } else if (!this.eventDetails.end_time) {
                this.addTimeLine({ diff: totalDuration }, 'Ongoing');
            } else {
                this.addTimeLine(this.timeDiff(callTraceStart, callTraceEnd), callTraceEnd);
            }
        },

        /**
         * Calculate diff between two dates
         * @param {string} start
         * @param {string} end
         * @return {{ diff, duration }} first value - time in milliseconds, second value - time in (HH:)mm:ss
         */
        timeDiff(start, end) {
            let diff = differenceInMilliseconds(parseISO(end), parseISO(start));
            let duration = secsToReadableTime(diff / 1000);

            return { diff, duration };
        },

        /**
         * Event duration in %
         * @param {string} endTime
         * @param {string} startTime
         * @param {number} totalDuration
         * @return {number}
         */
        getTimePercent(endTime, startTime, totalDuration) {
            let diff = differenceInMilliseconds(parseISO(endTime), parseISO(startTime));

            return (diff / totalDuration) * 100;
        },

        /**
         * Added new values in timeline array
         * @param {object} period
         * @param {string} time
         * @param {string} tooltip
         */
        addTimeLine({ diff, duration }, time, tooltip = '') {
            this.timelineTrace.push({
                beginning: diff,
                val:
                    time === 'Unknown'
                        ? this.$t('reports.unknown')
                        : time === 'Ongoing'
                        ? this.$t('reports.ongoing')
                        : duration,
                time: time,
                tooltip: tooltip === '' ? duration : tooltip,
                style: {
                    width: null,
                },
            });
        },

        /**
         * Returns width of speech in %.
         * @param {number} speechStart
         * @param {number} speechEnd
         * @return {{ width: number, marginLeft: number }}
         */
        speechStyle(speechStart, speechEnd) {
            let width = speechEnd - speechStart;
            let marginLeft = speechStart;
            // Display short speech
            if (width < MIN_WIDTH) {
                width = MIN_WIDTH;
            }

            return { width, marginLeft };
        },

        /**
         * Sets width for timeline blocks.
         * @param {number} totalDuration
         */
        setWidthForTimeLineTrace(totalDuration) {
            this.timelineTrace.forEach((trace, index, list) => {
                let nextTrace = list[index + 1];
                let width;
                if (nextTrace) {
                    let diff = (trace.beginning / totalDuration) * 100;
                    width = diff ? diff : 0;
                } else {
                    width = 100;
                }

                trace.style = {
                    width: `${width}%`,
                };
            });
        },

        /**
         * Resets values
         */
        resetToDefault() {
            this.eventDetails = [];
            this.isLoaded = false;
            this.np1 = [];
            this.np2 = [];
            this.speeches = [];
            this.timelineTrace = [];
        },

        /**
         * Checks if index is the first or the last
         * @param index
         * @returns {boolean}
         */
        isMainLine(index) {
            return index === 0 || index === this.timelineTrace.length - 1;
        },

        /**
         * Checks space for label of timeline
         * @param {object} trace
         * @param {number} index
         * @returns {boolean}
         */
        hasNeighbor(trace, index) {
            // Make sure that have previous trace;
            if (this.isMainLine(index)) {
                return false;
            }
            let preTrace = this.timelineTrace[index - 1];

            return parseInt(trace.style.width) - parseInt(preTrace.style.width) < 5;
        },

        /**
         * Checks space for label of nurse present event
         * @param {array} list
         * @param {number} index
         * @returns {boolean}
         */
        hasNPSpaceLimit(list, index) {
            // the last item
            if (list.length - 1 === index) {
                // Checks for over of 100%
                return parseFloat(list[index].style.left) + NP_WIDTH_LABEL > 100;
            }

            return (
                parseFloat(list[index + 1].style.left) - parseFloat(list[index].style.left) < NP_WIDTH_LABEL
            );
        },
    },
};
</script>
